// Sprint 20.3 PR-9 (D20 / UC-38) — runner indicator chip in ChatHeader.
//
// Tells the founder which runner is hosting the open agent's
// `claude` child. The auto-routing scheduler is opaque by default;
// this chip restores transparency without giving up the auto-pick
// benefit (per Santi's explicit ask 2026-05-08, captured in
// `docs/adr/D-013` D20).
//
// Render contract (mirrors `BranchChip` shape — `cursor-ux-mirror.md`
// §2.4 says "match what's there over inventing a new pattern"):
//
//   `🖥 R1`     — icon + short label
//
//   Tooltip (Cursor parity — calm, structured, no emoji):
//     Runner-N · acct-id · cap 78%
//
// Hide-when-null contract:
//   - No `runnerId` on the agent → return null (legacy row pre-S20.2).
//   - Snapshot not yet hydrated (`/api/runners` hasn't returned) →
//     render a minimal label-only chip with `R<n>` derived from id;
//     skip the cap-% / account fields. Founder still sees WHICH
//     runner, just not the live capacity yet.
//   - Snapshot hydrated but `cap_5h_residual_pct` is NULL → omit the
//     cap-% segment from the tooltip rather than showing "N/A". A
//     newly-provisioned runner that hasn't sent its first heartbeat
//     is in this state (D013-EC-60).
//
// Token discipline: identical token stack as `HeaderChip` in
// `ChatHeader.tsx` (text-muted-foreground, gap-1, max-w-[20ch], etc.).
// No raw values.

import { Icon } from './atoms/Icon';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from './ui/tooltip';
import { useTranslation } from '../i18n';
import { useRunnersStore, selectRunnerForAgentId } from '../stores/runners.store';
import type { RunnerSnapshot } from '../lib/api';

export interface RunnerChipProps {
  // The agent's pinned `runnerId` (e.g. `runner-vps1`,
  // `runner-runner-1`). Absent on legacy rows pre-S20.2; the chip
  // renders nothing in that case.
  runnerId: string | undefined;
}

export function RunnerChip({ runnerId }: RunnerChipProps) {
  const { t } = useTranslation();
  const snapshot = useRunnersStore((s) => selectRunnerForAgentId(s, runnerId));

  if (!runnerId) return null;

  const label = deriveRunnerLabel(runnerId);
  const tooltip = buildTooltip(t, runnerId, snapshot);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className="hidden sm:inline-flex items-center gap-1 shrink-0 max-w-[12ch] truncate"
          data-testid="runner-chip"
          data-runner-id={runnerId}
        >
          <Icon
            name="Server"
            size={12}
            className="shrink-0 text-muted-foreground/60"
            aria-hidden
          />
          <span className="text-xs truncate text-muted-foreground tabular-nums">
            {label}
          </span>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" data-testid="runner-chip-tooltip">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

// Strips the `runner-` prefix and projects the remaining segment to a
// short founder-mode label. Cases:
//
//   `runner-runner-1`   → `R1`
//   `runner-runner-12`  → `R12`
//   `runner-vps1`       → `VPS1`
//   `runner-vps2`       → `VPS2`
//   `runner-control`    → `CONTROL`
//   `runner-foo-bar`    → `FOO-BAR`
//
// Designed to be visually distinct between adjacent runners (operator
// looking at two open agents wants to tell at a glance whether they
// are on the same host or not). Keeps the hostname legible without
// surfacing the redundant `runner-` prefix.
export function deriveRunnerLabel(runnerId: string): string {
  const trimmed = runnerId.startsWith('runner-')
    ? runnerId.slice('runner-'.length)
    : runnerId;
  // `runner-runner-N` → `RN`. The CRM v2 + As Oasis convention until
  // S20.1.5 was to name hosts `runner-1`, `runner-2`, etc. — so the
  // canonicalised id `runner-runner-N` collapses to a single capital
  // R + numeric suffix.
  const numericRunner = /^runner-(\d+)$/.exec(trimmed);
  if (numericRunner) return `R${numericRunner[1]}`;
  return trimmed.toUpperCase();
}

type TranslateFn = ReturnType<typeof useTranslation>['t'];

function buildTooltip(
  t: TranslateFn,
  runnerId: string,
  snapshot: RunnerSnapshot | null,
): string {
  // No snapshot yet — show only what we know from the id.
  if (!snapshot) {
    return t('runnerChip.tooltipMinimal', { runner: runnerId });
  }
  const account = snapshot.claudeAccountId ?? t('runnerChip.accountUnknown');
  const cap =
    snapshot.cap5hResidualPct === null
      ? null
      : t('runnerChip.cap', { value: Math.round(100 - snapshot.cap5hResidualPct) });
  if (cap === null) {
    return t('runnerChip.tooltipNoCap', {
      hostname: snapshot.hostname,
      account,
    });
  }
  return t('runnerChip.tooltipFull', {
    hostname: snapshot.hostname,
    account,
    cap,
  });
}
